import React, { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Sheet,
  Table,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Snackbar,
} from '@mui/joy'
import {
  ArrowLeft as ArrowBack,
  PencilSimple as Edit,
  Envelope as Email,
  DownloadSimple as Download,
  Printer as Print,
  CreditCard as Payment,
  CheckCircle,
  Warning,
} from '@phosphor-icons/react'
import { invoicesApi, paymentsApi } from '../lib/billingApi'
import InvoicePaymentLink from '../components/billing/InvoicePaymentLink'
import SendDocumentEmailModal from '../components/billing/SendDocumentEmailModal'
import PaymentReceiptActions from '../components/billing/PaymentReceiptActions'
import type { PaymentRecord } from '../types/billing'
import { formatCurrency, formatStatus } from '../lib/billingUtils'
import type { BackendLineItem } from '../types/billing-backend'

export default function InvoiceDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarColor, setSnackbarColor] = useState<'success' | 'danger'>('success')
  
  // Fetch invoice details
  const { data: invoice, isLoading, error } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesApi.get(Number(id)),
    enabled: !!id,
  })
  
  // Fetch payment summary
  const { data: paymentSummary } = useQuery({
    queryKey: ['invoice-payments', id],
    queryFn: () => invoicesApi.paymentSummary(Number(id)),
    enabled: !!id,
  })
  
  // Removing a payment reverts the invoice to unpaid/partially paid and
  // unlocks amount editing; the payment can be re-recorded afterward.
  const removePaymentMutation = useMutation({
    mutationFn: (paymentId: number) => paymentsApi.delete(paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      queryClient.invalidateQueries({ queryKey: ['invoice-payments', id] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['payments'] })
      setSnackbarMessage('Payment removed. The invoice is unlocked for editing and can be re-paid.')
      setSnackbarColor('success')
      setSnackbarOpen(true)
    },
    onError: () => {
      setSnackbarMessage('Failed to remove payment. Please try again.')
      setSnackbarColor('danger')
      setSnackbarOpen(true)
    },
  })

  const handleRemovePayment = (payment: PaymentRecord) => {
    if (window.confirm(
      `Remove this ${formatCurrency(payment.amount)} payment?\n\n` +
      'The invoice will revert to unpaid (or partially paid) and its line items ' +
      'and amounts become editable again. You can re-record the payment afterward.'
    )) {
      removePaymentMutation.mutate(payment.id)
    }
  }

  const [showEmailModal, setShowEmailModal] = useState(false)

  // Mutations
  const markSentMutation = useMutation({
    mutationFn: () => invoicesApi.markSent(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
    },
  })

  const generatePdfMutation = useMutation({
    mutationFn: () => invoicesApi.generatePdf(Number(id)),
    onSuccess: (data) => {
      // Open PDF in new tab
      window.open(data.pdf_url, '_blank')
    },
  })
  
  // Generate display line items including overhead and profit
  const getDisplayLineItems = () => {
    if (!invoice) return []
    
    const items: (BackendLineItem & { is_system_generated?: boolean })[] = (invoice.line_items || []).map(item => ({
      ...item,
      item_type: item.item_type || 'service',
      is_taxable: item.is_taxable ?? false,
      sort_order: item.sort_order || 0,
      quantity: item.quantity,
      unit_price: item.unit_price,
      tax_rate: item.tax_rate || null,
      total: item.total,
      description: item.description,
      notes: item.notes,
      is_bold: item.is_bold
    }))
    
    // Add overhead as a line item if included
    if (invoice.include_overhead && invoice.overhead_amount && parseFloat(invoice.overhead_amount) > 0) {
      items.push({
        description: `Overhead (${invoice.overhead_percentage}%)`,
        notes: 'Calculated based on subtotal',
        quantity: 1,
        unit_price: invoice.overhead_amount,
        item_type: 'other',
        tax_rate: null,
        is_taxable: false,
        total: invoice.overhead_amount,
        sort_order: 9998,
        is_system_generated: true,
      })
    }
    
    // Add profit as a line item if included
    if (invoice.include_profit && invoice.profit_amount && parseFloat(invoice.profit_amount) > 0) {
      items.push({
        description: `Profit (${invoice.profit_percentage}%)`,
        notes: 'Calculated based on subtotal',
        quantity: 1,
        unit_price: invoice.profit_amount,
        item_type: 'other',
        tax_rate: null,
        is_taxable: false,
        total: invoice.profit_amount,
        sort_order: 9999,
        is_system_generated: true,
      })
    }
    
    return items
  }
  
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    )
  }
  
  if (error || !invoice) {
    return (
      <Alert color="danger" sx={{ m: 2 }}>
        Error loading invoice. Please try again.
      </Alert>
    )
  }
  
  
  const getStatusColor = (status: string): any => {
    const statusColors = {
      draft: 'neutral',
      sent: 'primary',
      viewed: 'primary',
      paid: 'success',
      partially_paid: 'warning',
      overdue: 'danger',
      cancelled: 'neutral',
    }
    return statusColors[status as keyof typeof statusColors] || 'neutral'
  }
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          component={Link}
          to=".."
          relative="path"
          startDecorator={<ArrowBack />}
          variant="plain"
          size="sm"
          sx={{ mb: 2 }}
        >
          Back to Invoices
        </Button>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'flex-start' },
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              level="h1"
              sx={{
                mb: 1,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.25rem' },
                wordBreak: 'break-word',
              }}
            >
              Invoice {invoice.document_number}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Chip
                size="lg"
                color={getStatusColor(invoice.status)}
                variant="soft"
              >
                {formatStatus(invoice.status)}
              </Chip>
              {invoice.days_overdue && invoice.days_overdue > 0 && (
                <Chip
                  size="sm"
                  color="danger"
                  variant="outlined"
                  startDecorator={<Warning />}
                >
                  {invoice.days_overdue} days overdue
                </Chip>
              )}
              {invoice.payment_status === 'paid' && (
                <Chip
                  size="sm"
                  color="success"
                  variant="soft"
                  startDecorator={<CheckCircle />}
                >
                  Paid in Full
                </Chip>
              )}
            </Stack>
          </Box>
          
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ '& > *': { flex: { xs: '1 1 auto', md: '0 0 auto' } } }}
          >
            {invoice.status === 'draft' && (
              <Button
                variant="solid"
                color="primary"
                onClick={() => markSentMutation.mutate()}
                loading={markSentMutation.isPending}
              >
                Mark as Sent
              </Button>
            )}
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<Email />}
              onClick={() => setShowEmailModal(true)}
            >
              Send Email
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<Download />}
              onClick={() => generatePdfMutation.mutate()}
              loading={generatePdfMutation.isPending}
            >
              Download PDF
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startDecorator={<Print />}
              onClick={() => window.print()}
            >
              Print
            </Button>
            {invoice.status !== 'void' && invoice.status !== 'cancelled' && (
              <Button
                component={Link}
                to={`/operations/billing/invoices/${id}/edit`}
                variant="outlined"
                color="primary"
                startDecorator={<Edit />}
              >
                Edit
              </Button>
            )}
          </Stack>
        </Box>
      </Box>

      {/* Main Content */}
      <Tabs defaultValue={0}>
        <TabList>
          <Tab>Invoice Details</Tab>
          <Tab>Payments ({paymentSummary?.payments?.length || invoice?.payments?.length || 0})</Tab>
          <Tab>Activity</Tab>
        </TabList>
        
        <TabPanel value={0}>
          <Grid container spacing={3}>
            {/* Invoice Info */}
            <Grid xs={12} lg={8}>
              <Card variant="outlined">
                <CardContent>
                  {/* Customer & Job Info */}
                  <Box sx={{ mb: 3 }}>
                    <Grid container spacing={3}>
                      <Grid xs={12} sm={6}>
                      <Typography level="title-sm" sx={{ mb: 1 }}>Bill To</Typography>
                      <Typography level="body-md" fontWeight="lg">
                        {invoice.customer_detail ? 
                          `${invoice.customer_detail.first_name} ${invoice.customer_detail.last_name}` : 
                          'N/A'
                        }
                      </Typography>
                      {invoice.customer_detail?.company_name && (
                        <Typography level="body-sm">{invoice.customer_detail.company_name}</Typography>
                      )}
                      {invoice.customer_detail?.email && (
                        <Typography level="body-sm">{invoice.customer_detail.email}</Typography>
                      )}
                      {invoice.customer_detail?.phone && (
                        <Typography level="body-sm">{invoice.customer_detail.phone}</Typography>
                      )}
                    </Grid>
                    
                    {invoice.job_detail && (
                      <Grid xs={12} sm={6}>
                        <Typography level="title-sm" sx={{ mb: 1 }}>Related Job</Typography>
                        <Typography level="body-md">{invoice.job_detail.job_number}</Typography>
                        {invoice.job_detail.address_1 && (
                          <Typography level="body-sm">{invoice.job_detail.address_1}, {invoice.job_detail.city}</Typography>
                        )}
                      </Grid>
                    )}
                  </Grid>
                  </Box>
                  
                  <Divider sx={{ my: 3 }} />
                  
                  {/* Line Items */}
                  <Typography level="title-md" sx={{ mb: 2 }}>Line Items</Typography>
                  <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 600 }}>
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th style={{ width: 100 }}>Qty</th>
                          <th style={{ width: 120 }}>Unit Price</th>
                          <th style={{ width: 120 }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getDisplayLineItems().map((item, index) => (
                          <React.Fragment key={index}>
                            <tr
                              style={{
                                backgroundColor: item.is_system_generated ? '#f5f5f5' : 'transparent'
                              }}
                            >
                              <td>
                                <Box>
                                  <Typography
                                    level="body-sm"
                                    sx={{
                                      fontStyle: item.is_system_generated ? 'italic' : 'normal'
                                    }}
                                  >
                                    {item.description}
                                  </Typography>
                                  {item.notes && (
                                    <Typography
                                      level="body-xs"
                                      sx={{
                                        mt: 0.5,
                                        color: 'text.secondary',
                                        fontStyle: 'italic'
                                      }}
                                    >
                                      {item.notes}
                                    </Typography>
                                  )}
                                </Box>
                              </td>
                              <td>
                                <Typography
                                  level="body-sm"
                                  sx={{
                                    fontStyle: item.is_system_generated ? 'italic' : 'normal'
                                  }}
                                >
                                  {item.quantity}
                                </Typography>
                              </td>
                              <td>
                                <Typography
                                  level="body-sm"
                                  sx={{
                                    fontStyle: item.is_system_generated ? 'italic' : 'normal'
                                  }}
                                >
                                  {formatCurrency(item.unit_price)}
                                </Typography>
                              </td>
                              <td>
                                <Typography
                                  level="body-sm"
                                  sx={{
                                    fontStyle: item.is_system_generated ? 'italic' : 'normal'
                                  }}
                                >
                                  {formatCurrency(item.total)}
                                </Typography>
                              </td>
                            </tr>
                            {/* Internal sub-line-items (org-only, never shown to customer) */}
                            {(item.sub_items ?? []).map((sub, subIndex) => (
                              <tr
                                key={`sub-${index}-${subIndex}`}
                                style={{ backgroundColor: '#fafafa' }}
                              >
                                <td style={{ paddingLeft: 32 }}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <Chip size="sm" variant="soft" color="neutral">Internal</Chip>
                                    <Typography level="body-xs" textColor="text.secondary">
                                      {sub.description}
                                    </Typography>
                                  </Stack>
                                </td>
                                <td>
                                  <Typography level="body-xs" textColor="text.secondary">
                                    {sub.quantity}
                                  </Typography>
                                </td>
                                <td>
                                  <Typography level="body-xs" textColor="text.secondary">
                                    {formatCurrency(sub.unit_price)}
                                  </Typography>
                                </td>
                                <td>
                                  <Typography level="body-xs" textColor="text.secondary">
                                    {formatCurrency(sub.total)}
                                  </Typography>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={3} style={{ textAlign: 'right' }}>
                            <Typography level="body-sm">Subtotal</Typography>
                          </td>
                          <td>
                            <Typography level="body-sm" fontWeight="lg">
                              {formatCurrency(invoice.subtotal)}
                            </Typography>
                          </td>
                        </tr>
                        {parseFloat(invoice.discount_amount) > 0 && (
                          <tr>
                            <td colSpan={3} style={{ textAlign: 'right' }}>
                              <Typography level="body-sm">
                                Discount {invoice.discount_percentage && `(${invoice.discount_percentage}%)`}
                              </Typography>
                            </td>
                            <td>
                              <Typography level="body-sm" color="success">
                                -{formatCurrency(invoice.discount_amount)}
                              </Typography>
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td colSpan={3} style={{ textAlign: 'right' }}>
                            <Typography level="body-sm">Tax</Typography>
                          </td>
                          <td>
                            <Typography level="body-sm">
                              {formatCurrency(invoice.tax_amount)}
                            </Typography>
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3} style={{ textAlign: 'right' }}>
                            <Typography level="body-md" fontWeight="lg">Total</Typography>
                          </td>
                          <td>
                            <Typography level="body-md" fontWeight="lg">
                              {formatCurrency(invoice.total_amount)}
                            </Typography>
                          </td>
                        </tr>
                      </tfoot>
                    </Table>
                  </Sheet>
                  
                  {/* Notes & Terms */}
                  {(invoice.notes || invoice.terms) && (
                    <>
                      <Divider sx={{ my: 3 }} />
                      <Grid container spacing={3}>
                        {invoice.notes && (
                          <Grid xs={12} md={6}>
                            <Typography level="title-sm" sx={{ mb: 1 }}>Notes</Typography>
                            <Typography level="body-sm">{invoice.notes}</Typography>
                          </Grid>
                        )}
                        {invoice.terms && (
                          <Grid xs={12} md={6}>
                            <Typography level="title-sm" sx={{ mb: 1 }}>Terms & Conditions</Typography>
                            <Typography level="body-sm">{invoice.terms}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Sidebar */}
            <Grid xs={12} lg={4}>
              <Stack spacing={3}>
                {/* Invoice Summary */}
                <Card variant="outlined">
                  <CardContent>
                    <Typography level="title-md" sx={{ mb: 2 }}>Invoice Summary</Typography>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography level="body-sm">Invoice Date</Typography>
                        <Typography level="body-sm" fontWeight="lg">
                          {formatDate(invoice.document_date)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography level="body-sm">Due Date</Typography>
                        <Typography level="body-sm" fontWeight="lg">
                          {invoice.due_date ? formatDate(invoice.due_date) : 'N/A'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography level="body-sm">Total Amount</Typography>
                        <Typography level="body-md" fontWeight="lg">
                          {formatCurrency(invoice.total_amount)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography level="body-sm">Amount Paid</Typography>
                        <Typography level="body-md" fontWeight="lg" color="success">
                          {formatCurrency(invoice.total_paid || 0)}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography level="body-sm">Balance Due</Typography>
                        <Typography 
                          level="body-md" 
                          fontWeight="lg"
                          color={parseFloat(invoice.balance_due || '0') > 0 ? 'warning' : 'success'}
                        >
                          {formatCurrency(invoice.balance_due || 0)}
                        </Typography>
                      </Box>
                    </Stack>
                    
                    <InvoicePaymentLink url={invoice.payment_url} />
                    {parseFloat(invoice.balance_due || '0') > 0 && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Button
                          component={Link}
                          to={`/operations/billing/payments/new?invoice=${invoice.id}`}
                          fullWidth
                          variant="solid"
                          color="primary"
                          startDecorator={<Payment />}
                        >
                          Record Payment
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
                
                {/* Timeline */}
                {(invoice.sent_date || invoice.viewed_date || invoice.paid_date) && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography level="title-md" sx={{ mb: 2 }}>Timeline</Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography level="body-sm" fontWeight="lg">Created</Typography>
                          <Typography level="body-sm">
                            {formatDate(invoice.created_at)}
                          </Typography>
                        </Box>
                        {invoice.sent_date && (
                          <Box>
                            <Typography level="body-sm" fontWeight="lg">Sent</Typography>
                            <Typography level="body-sm">
                              {formatDate(invoice.sent_date)}
                            </Typography>
                          </Box>
                        )}
                        {invoice.viewed_date && (
                          <Box>
                            <Typography level="body-sm" fontWeight="lg">Viewed</Typography>
                            <Typography level="body-sm">
                              {formatDate(invoice.viewed_date)}
                            </Typography>
                          </Box>
                        )}
                        {invoice.paid_date && (
                          <Box>
                            <Typography level="body-sm" fontWeight="lg" color="success">Paid</Typography>
                            <Typography level="body-sm">
                              {formatDate(invoice.paid_date)}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                )}
              </Stack>
            </Grid>
          </Grid>
        </TabPanel>
        
        <TabPanel value={1}>
          {/* Payments Tab */}
          <Card variant="outlined">
            <CardContent>
              {/* Use paymentSummary if available, otherwise fall back to invoice.payments */}
              {((paymentSummary?.payments || invoice?.payments) && (paymentSummary?.payments || invoice?.payments)!.length > 0) ? (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflowX: 'auto' }}>
                  <Table sx={{ minWidth: 800 }}>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Method</th>
                        <th>Reference</th>
                        <th>Notes</th>
                        <th style={{ width: 250 }}>Receipt & actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(paymentSummary?.payments || invoice?.payments || []).map((payment: PaymentRecord) => (
                        <tr key={payment.id}>
                          <td>{formatDate(payment.payment_date)}</td>
                          <td>{formatCurrency(payment.amount)}</td>
                          <td>{payment.payment_method}</td>
                          <td>{payment.reference_number || '-'}</td>
                          <td>{payment.notes || '-'}</td>
                          <td>
                            <PaymentReceiptActions paymentId={payment.id} />
                            <Button
                              size="sm"
                              variant="plain"
                              color="danger"
                              loading={removePaymentMutation.isPending && removePaymentMutation.variables === payment.id}
                              onClick={() => handleRemovePayment(payment)}
                              sx={{ mt: 1 }}
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Sheet>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography level="body-lg" sx={{ mb: 2 }}>No payments recorded</Typography>
                  {parseFloat(invoice.balance_due || '0') > 0 && (
                    <Button
                      component={Link}
                      to={`/operations/billing/payments/new?invoice=${invoice.id}`}
                      variant="solid"
                      startDecorator={<Payment />}
                    >
                      Record Payment
                    </Button>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </TabPanel>
        
        <TabPanel value={2}>
          {/* Activity Tab */}
          <Card variant="outlined">
            <CardContent>
              <Typography level="body-md" sx={{ textAlign: 'center', py: 4 }}>
                Activity tracking coming soon
              </Typography>
            </CardContent>
          </Card>
        </TabPanel>
      </Tabs>
      
      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        color={snackbarColor}
        variant="solid"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbarMessage}
      </Snackbar>

      <SendDocumentEmailModal
        open={showEmailModal}
        kind="invoice"
        documentId={id ? Number(id) : null}
        onClose={() => setShowEmailModal(false)}
        onSent={() => queryClient.invalidateQueries({ queryKey: ['invoice', id] })}
      />
    </Box>
  )
}

// Grid component
const Grid: React.FC<{
  children: React.ReactNode
  container?: boolean
  spacing?: number
  xs?: number
  sm?: number
  md?: number
  lg?: number
}> = ({ children, container, spacing = 0, xs, sm, md, lg }) => {
  if (container) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          margin: spacing ? -spacing / 2 : 0,
        }}
      >
        {children}
      </Box>
    )
  }
  
  return (
    <Box
      sx={{
        padding: spacing ? spacing / 2 : 0,
        width: {
          xs: xs ? `${(xs / 12) * 100}%` : '100%',
          sm: sm ? `${(sm / 12) * 100}%` : undefined,
          md: md ? `${(md / 12) * 100}%` : undefined,
          lg: lg ? `${(lg / 12) * 100}%` : undefined,
        },
      }}
    >
      {children}
    </Box>
  )
}
